import CommonIriDisplay from '../application-core/CommonIriDisplay';
import { Typography } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import { withStyles } from '@material-ui/core/styles';
import ArrowLeftIcon from '@material-ui/icons/ArrowLeft';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import QualityCheck from '../application-core/QualityCheck';
import LanguageAvatar from '../core/LanguageAvatar';
import { globalStyles as styles } from '../core/style/gstheme';
import QualityCheckLoader from '../application-core/QualityCheckLoader';
import { checkResourceEntitlements, getProjectFromGraph } from '../core/utils/utils';

const views = [
    "disjointclassesresources",
    "disjointpropertiesresources",
    "multiplepropvaluesresources",
    "associatedpropertyrangeresources",
    "asymmetricresources"
];

const viewsRequiringOntologies = [
    "disjointpropertiesresources",
    "multiplepropvaluesresources",
    "associatedpropertyrangeresources",
    "asymmetricresources"
];

class DatasetQuality extends React.Component {

    constructor(props) {
        super(props);
        // We filter out views that require ontologies if no associated projects
        const filteredViews = views
            .filter(view => !viewsRequiringOntologies.includes(view)
                || (this.props.ontologyProjectAssociation && this.props.ontologyProjectAssociation.length > 0));
        this.state = {
            filteredViews
        };
    }

    displayResults = (issues, isLoading, onRefresh) => {
        const { classes, ontologyProjectAssociation, graph, defaultLang, onToggleView, onDisable, data, entitlement } = this.props;
        const disabledFeatures = data.disabledFeatures;
        const isReadOnly = checkResourceEntitlements(data, entitlement);
        return <div>
            <QualityCheck
                dataTest="disjoint-classes"
                title="Disjoint Classes on the Same Resource"
                issues={issues.disjointclassesresources}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                loadProps={["id", "disjointClassViolation"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-disjointclassesresources")}
                onDisable={(disable) => onDisable("disjointclassesresources", disable)}
                onRefresh={() => onRefresh("disjointclassesresources")}
                errors={issues["disjointclassesresources-error"]}
                fromCache={issues["disjointclassesresources-time"]}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">Disjoint classes means that no resource should have both classes, as it is not possible to be both classes at the same time.</Typography>
                    <Typography variant="body2" color="textPrimary">You can possibly resolve these issues by removing one of the classes on each resource, or checking whether the classes should actually be disjoint in the ontology.</Typography>
                </div>}
                issueDisplay={
                    (dw) => {
                        return dw.disjointClassViolation &&
                            <div style={{ marginBottom: 10 }}>
                                <CommonIriDisplay
                                    id={dw.id}
                                    compact={true}
                                    projectIRI={getProjectFromGraph(graph)}
                                    handleNotAvailable={true}
                                    loadComponents={true}
                                    graph={graph}
                                    wrap={false}
                                    onLink={onToggleView}
                                    viewLang={defaultLang}
                                    defaultLang={defaultLang} />
                                <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                {dw.disjointClassViolation.sort().map(v =>
                                    <Chip key={v} color="primary" className={classes.inactiveChip} label={v} />
                                )}
                            </div>;
                    }}
            />
            {ontologyProjectAssociation &&
                <QualityCheck
                    dataTest="disjoint-properties"
                    title="Clashing Values on Disjoint Properties on Resources"
                    issues={issues.disjointpropertiesresources}
                    isLoading={isLoading}
                    graph={graph}
                    isReadOnly={isReadOnly}
                    sortLabel="label"
                    defaultLang={defaultLang}
                    loadProps={["id", "disjointPropertyViolation"]}
                    disabled={disabledFeatures && disabledFeatures.includes("quality-disjointpropertiesresources")}
                    onDisable={(disable) => onDisable("disjointpropertiesresources", disable)}
                    onRefresh={() => onRefresh("disjointpropertiesresources")}
                    errors={issues["disjointpropertiesresources-error"]}
                    fromCache={issues["disjointpropertiesresources-time"]}
                    meaning={<div>
                        <Typography variant="body2" color="textPrimary">Disjoint properties means that those properties should not have the same value at the same time.</Typography>
                        <Typography variant="body2" color="textPrimary">You can possibly resolve these issues by removing one of the values, or checking whether the properties should actually be disjoint in the ontology.</Typography>
                    </div>}
                    issueDisplay={
                        (dp, issues) => {
                            return dp.disjointPropertyViolation &&
                                <div style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        id={dp.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                    <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    {dp.disjointPropertyViolation.sort().map(v => {
                                        const propertyItem = issues.find(item => item.id === v);
                                        // Just allow for edge case of same IRI in two projects!
                                        const propertyGraph = Array.isArray(propertyItem.inNamedGraph) ?
                                            propertyItem.inNamedGraph[0]
                                            :
                                            propertyItem.inNamedGraph;
                                        return <CommonIriDisplay
                                            id={v}
                                            key={v}
                                            compact={true}
                                            projectIRI={getProjectFromGraph(propertyGraph)}
                                            handleNotAvailable={true}
                                            loadComponents={true}
                                            graph={propertyGraph}
                                            wrap={false}
                                            onLink={onToggleView}
                                            viewLang={defaultLang}
                                            defaultLang={defaultLang} />;
                                    })}
                                </div>;
                        }}
                />
            }
            {ontologyProjectAssociation &&
                <QualityCheck
                    dataTest="multiple-property-values"
                    title="Multiple Property Values for a Functional Property on a Resource"
                    issues={issues.multiplepropvaluesresources}
                    isLoading={isLoading}
                    graph={graph}
                    isReadOnly={isReadOnly}
                    defaultLang={defaultLang}
                    loadProps={["id", "multiplePropValueViolation"]}
                    disabled={disabledFeatures && disabledFeatures.includes("quality-multiplepropvaluesresources")}
                    onDisable={(disable) => onDisable("multiplepropvaluesresources", disable)}
                    onRefresh={() => onRefresh("multiplepropvaluesresources")}
                    errors={issues["multiplepropvaluesresources-error"]}
                    fromCache={issues["multiplepropvaluesresources-time"]}
                    meaning={<div>
                        <Typography variant="body2" color="textPrimary">Functional properties should only have one value.</Typography>
                        <Typography variant="body2" color="textPrimary">The following resources have multiple values for the same property.</Typography>
                        <Typography variant="body2" color="textPrimary">You can resolve these issues by removing the additional values.</Typography>
                    </div>}
                    issueDisplay={
                        (li, issues) => {
                            return li.multiplePropValueViolation &&
                                <div key={li.id} style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        id={li.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                    <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    {li.multiplePropValueViolation.sort().map((v, vIndex) => {
                                        const labelItem = issues.find(item => item.id === v);
                                        const labelLang = Object.keys(labelItem.label)[0];
                                        const label = labelItem ?
                                            labelItem.label[labelLang]
                                            :
                                            v.latestIRI || v;
                                        return labelLang ?
                                            <Chip
                                                data-test={"value-" + vIndex}
                                                color="primary"
                                                className={classes.labelChip}
                                                key={"chip-" + v}
                                                avatar={<LanguageAvatar lang={labelLang} />}
                                                label={label} />
                                            :
                                            <Chip key={v} color="primary" className={classes.inactiveChip} label={label} />;
                                    }
                                    )}
                                </div>;
                        }
                    }
                />
            }
            {ontologyProjectAssociation &&
                <QualityCheck
                    dataTest="associated-property"
                    title="Associated Property Range Integrity on a Resource"
                    issues={issues.associatedpropertyrangeresources}
                    isLoading={isLoading}
                    graph={graph}
                    isReadOnly={isReadOnly}
                    defaultLang={defaultLang}
                    loadProps={["id", "inconsistentWithRange"]}
                    disabled={disabledFeatures && disabledFeatures.includes("quality-associatedpropertyrangeresources")}
                    onDisable={(disable) => onDisable("associatedpropertyrangeresources", disable)}
                    onRefresh={() => onRefresh("associatedpropertyrangeresources")}
                    errors={issues["associatedpropertyrangeresources-error"]}
                    fromCache={issues["associatedpropertyrangeresources-time"]}
                    meaning={<div>
                        <Typography variant="body2" color="textPrimary">If a property has a range it is expected that the target resource will have the range as a class.</Typography>
                        <Typography variant="body2" color="textPrimary">The following resources use an associated property whose target does not have classes that are stated on the range of the associated property.</Typography>
                        <Typography variant="body2" color="textPrimary">You can probably resolve these issues by adding the class to the target, or alternatively, check the range on the property.</Typography>
                    </div>}
                    issueDisplay={
                        (iwr) => {
                            return iwr.inconsistentWithRange && iwr.inconsistentWithRange.map(v =>
                                <div key={v} style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        id={iwr.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                    <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    <Chip color="primary"
                                        data-test="target-property"
                                        className={classes.inactiveChip}
                                        label={v} />
                                </div>
                            );
                        }
                    }
                />
            }
            {ontologyProjectAssociation &&
                <QualityCheck
                    dataTest="asymmetric"
                    title="Asymmetric Relationship Integrity on a Resource"
                    issues={issues.asymmetricresources}
                    isLoading={isLoading}
                    graph={graph}
                    isReadOnly={isReadOnly}
                    defaultLang={defaultLang}
                    loadProps={["id", "asymmetricViolation"]}
                    disabled={disabledFeatures && disabledFeatures.includes("quality-asymmetricresources")}
                    onDisable={(disable) => onDisable("asymmetricresources", disable)}
                    onRefresh={() => onRefresh("asymmetricresources")}
                    errors={issues["asymmetricresources-error"]}
                    fromCache={issues["asymmetricresources-time"]}
                    meaning={<div>
                        <Typography variant="body2" color="textPrimary">An asymmetric property implies that the relationship should not hold in both directions.</Typography>
                        <Typography variant="body2" color="textPrimary">The following resources are connected in both directions by an asymmetric property.</Typography>
                        <Typography variant="body2" color="textPrimary">You can probably resolve these issues by removing the relationship in one direction.</Typography>
                    </div>}
                    issueDisplay={
                        (asy) => {
                            return asy.asymmetricViolation &&
                                <div style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        id={asy.id}
                                        key={asy.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                    <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    {asy.asymmetricViolation.sort().map(v => {
                                        return <CommonIriDisplay
                                            id={v}
                                            key={v}
                                            compact={true}
                                            projectIRI={getProjectFromGraph(graph)}
                                            handleNotAvailable={true}
                                            loadComponents={true}
                                            graph={graph}
                                            wrap={false}
                                            onLink={onToggleView}
                                            viewLang={defaultLang}
                                            defaultLang={defaultLang} />;
                                    })}
                                </div>;
                        }
                    }
                />
            }
        </div>;
    };

    render() {

        const { filteredViews } = this.state;
        const { id, graph, data } = this.props;

        return (
            <div>
                <QualityCheckLoader
                    id={id}
                    graph={graph}
                    disabledFeatures={data.disabledFeatures}
                    displayResults={this.displayResults}
                    views={filteredViews} />
            </div>
        );
    }
}

DatasetQuality.propTypes = {
    onToggleView: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    onDisable: PropTypes.func.isRequired,
    link: PropTypes.string.isRequired,
    history: PropTypes.object,
    ontologyProjectAssociation: PropTypes.array,
    entitlement: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(withRouter(DatasetQuality));